import { useState, useCallback, useEffect, Suspense } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  LinearProgress,
  TextField,
  ButtonGroup,
} from '@mui/material';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useParams } from 'react-router-dom';
import TerrainMap from '../components/TerrainMap';

// Placeholder 3D Stupa Component
const StupaMesh = () => {
  return (
    <mesh>
      <coneGeometry args={[1, 2, 32]} />
      <meshStandardMaterial color="#4CAF50" />
    </mesh>
  );
};

const StupaSupport = () => {
  const { id } = useParams();
  const [mapBlur, setMapBlur] = useState(8);
  const [donationAmount, setDonationAmount] = useState<string>('');
  const [customAmount, setCustomAmount] = useState(false);

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY;
    const threshold = 300;
    setMapBlur(scrollPosition > threshold ? 0 : 8);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // This would come from your API based on the ID - using similar structure to TerrainMap
  const stupaData = {
    id: '2',
    name: 'Potential Site Beta',
    estimatedWaterSavings: 8000,
    donationGoal: 12000,
    currentDonations: 3500,
    description: 'This location offers ideal conditions for an ice stupa with high elevation, consistent water access, and proximity to agricultural areas.'
  };

  const predefinedAmounts = [1, 5, 20, 50];
  const progressPercentage = (stupaData.currentDonations / stupaData.donationGoal) * 100;

  const handleAmountSelect = (amount: number) => {
    setDonationAmount(amount.toString());
    setCustomAmount(false);
  };

  const handleCustomAmount = () => {
    setCustomAmount(true);
    setDonationAmount('');
  };

  return (
    <Box component="div" sx={{ position: 'relative', minHeight: '100vh' }}>
      <Box
        component="div"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          filter: `blur(${mapBlur}px)`,
          transition: 'filter 0.5s ease',
          pointerEvents: mapBlur === 0 ? 'auto' : 'none'
        }}
      >
        <TerrainMap />
      </Box>
      
      <Container 
        maxWidth="lg" 
        sx={{ 
          paddingTop: '80px',
          position: 'relative',
          zIndex: 1
        }}
      >
        <Grid container spacing={4}>
          {/* Left Column - Main Info */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h3" sx={{ mb: 2 }}>
                {stupaData.name}
              </Typography>
              
              {/* 3D Model Viewer */}
              <Box 
                component="div"
                sx={{ 
                  mb: 3,
                  height: '350px',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  bgcolor: '#f8f9fa'
                }}
              >
                <Canvas camera={{ position: [0, 2, 5] }}>
                  <ambientLight intensity={0.5} />
                  <pointLight position={[10, 10, 10]} />
                  <Suspense fallback={null}>
                    <StupaMesh />
                  </Suspense>
                  <OrbitControls />
                </Canvas>
              </Box>

              {/* Site Analysis Section */}
              <Box component="div" sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#4CAF50' }}>
                  Site Analysis
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 3 }}>
                      <Typography variant="h6" color="#4CAF50" gutterBottom>Environmental Factors</Typography>
                      <Typography variant="body1" paragraph>
                        • Elevation: 3,450m above sea level
                      </Typography>
                      <Typography variant="body1" paragraph>
                        • Average Winter Temperature: -5°C
                      </Typography>
                      <Typography variant="body1" paragraph>
                        • Annual Precipitation: 750mm
                      </Typography>
                      <Typography variant="body1">
                        • Natural Water Sources: 2 mountain streams
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 3 }}>
                      <Typography variant="h6" color="#4CAF50" gutterBottom>Community Impact</Typography>
                      <Typography variant="body1" paragraph>
                        • Villages Served: 3 local communities
                      </Typography>
                      <Typography variant="body1" paragraph>
                        • Agricultural Land: 250 hectares
                      </Typography>
                      <Typography variant="body1" paragraph>
                        • Beneficiary Families: 120+
                      </Typography>
                      <Typography variant="body1">
                        • Main Crops: Wheat, Barley, Vegetables
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {/* Timeline Section */}
              <Box component="div" sx={{ mb: 3 }}>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#4CAF50' }}>
                  Project Timeline
                </Typography>
                <Box component="div" sx={{ position: 'relative', padding: 2 }}>
                  {/* Timeline items */}
                  {[
                    { date: 'October 2023', title: 'Site Selection', description: 'Location identified and initial surveys completed' },
                    { date: 'December 2023', title: 'Community Engagement', description: 'Local stakeholder meetings and agreement signing' },
                    { date: 'February 2024', title: 'Construction Start', description: 'Foundation work and pipe system installation' },
                    { date: 'June 2024', title: 'Completion Target', description: 'First ice formation cycle begins' }
                  ].map((item, index) => (
                    <Box 
                      key={index} 
                      component="div"
                      sx={{ 
                        display: 'flex', 
                        marginBottom: 3, 
                        alignItems: 'flex-start' 
                      }}
                    >
                      <Box 
                        component="div"
                        sx={{ 
                          width: '120px', 
                          flexShrink: 0,
                          color: '#4CAF50',
                          fontWeight: 600 
                        }}
                      >
                        {item.date}
                      </Box>
                      <Box 
                        component="div"
                        sx={{ 
                          marginLeft: 2,
                          paddingBottom: 3,
                          borderLeft: '2px solid #4CAF50',
                          paddingLeft: 3,
                          position: 'relative'
                        }}
                      >
                        <Typography variant="h6" gutterBottom>{item.title}</Typography>
                        <Typography variant="body1" color="text.secondary">
                          {item.description}
                        </Typography>
                        <Box 
                          component="div"
                          sx={{
                            position: 'absolute',
                            left: '-7px',
                            top: '0',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            bgcolor: '#4CAF50'
                          }} 
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Donation Panel */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ 
              p: 3,
              position: { md: 'sticky' }, 
              top: 90,
            }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#4CAF50' }}>
                Support This Project
              </Typography>

              {/* Progress Bar */}
              <Box component="div" sx={{ mb: 3 }}>
                <Typography sx={{ mb: 1, fontWeight: 600 }}>Funding Progress</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={progressPercentage}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    mb: 1,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#4CAF50'
                    }
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  ${stupaData.currentDonations} raised of ${stupaData.donationGoal} goal
                </Typography>
              </Box>

              {/* Recent Donations */}
              <Box component="div" sx={{ mb: 3 }}>
                <Typography sx={{ mb: 2, fontWeight: 600 }}>Recent Donations</Typography>
                {[
                  { name: 'Anonymous', amount: 50, time: '2 hours ago' },
                  { name: 'Sarah M.', amount: 100, time: '5 hours ago' },
                  { name: 'John D.', amount: 25, time: '1 day ago' },
                  { name: 'Anonymous', amount: 75, time: '2 days ago' }
                ].map((donation, index) => (
                  <Box 
                    key={index} 
                    component="div"
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '12px',
                      paddingBottom: '12px',
                      borderBottom: index !== 3 ? '1px solid #eee' : 'none'
                    }}
                  >
                    <Box component="div">
                      <Typography variant="body1">{donation.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{donation.time}</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 500 }}>
                      ${donation.amount}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Donation Buttons */}
              <ButtonGroup variant="outlined" fullWidth sx={{ mb: 2 }}>
                {predefinedAmounts.map((amount) => (
                  <Button
                    key={amount}
                    onClick={() => handleAmountSelect(amount)}
                    sx={{
                      color: donationAmount === amount.toString() ? 'white' : '#4CAF50',
                      bgcolor: donationAmount === amount.toString() ? '#4CAF50' : 'transparent',
                      borderColor: '#4CAF50',
                      '&:hover': {
                        bgcolor: '#45a049',
                        color: 'white',
                        borderColor: '#45a049',
                      }
                    }}
                  >
                    ${amount}
                  </Button>
                ))}
              </ButtonGroup>

              {/* Custom Amount Button and Input */}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCustomAmount}
                sx={{
                  color: customAmount ? 'white' : '#4CAF50',
                  bgcolor: customAmount ? '#4CAF50' : 'transparent',
                  borderColor: '#4CAF50',
                  mb: 2,
                  '&:hover': {
                    bgcolor: '#45a049',
                    color: 'white',
                    borderColor: '#45a049',
                  }
                }}
              >
                Custom Amount
              </Button>

              {customAmount && (
                <TextField
                  fullWidth
                  label="Enter amount"
                  variant="outlined"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  type="number"
                  InputProps={{
                    startAdornment: '$',
                    inputProps: { min: 0 }
                  }}
                  sx={{ mb: 2 }}
                />
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  bgcolor: '#4CAF50',
                  color: 'white',
                  py: 2,
                  '&:hover': {
                    bgcolor: '#45a049'
                  }
                }}
              >
                Complete Donation
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default StupaSupport; 